import { useEffect, useRef } from "react"
import type { Ampel } from "@/lib/pruefung"
import { AMPEL_TEXT } from "@/lib/pruefung"
import { cn } from "@/lib/format"

const AMPEL_HINTERGRUND: Record<Exclude<Ampel, null>, string> = {
  gruen: "var(--ampel-gruen)",
  gelb: "var(--ampel-gelb)",
  rot: "var(--ampel-rot)",
}

interface Props {
  label: string
  einheit?: string
  wert: string
  aktiv: boolean
  onWaehlen: () => void
  ampel?: Ampel
  platzhalter?: string
  geaendert?: boolean
  gross?: boolean
}

/** Ein Zahlenfeld zum Antippen; Eingabe über das Tastenfeld. Ampel mit Farbe UND Text. */
export function Feld({ label, einheit, wert, aktiv, onWaehlen, ampel, platzhalter, geaendert, gross }: Props) {
  const ref = useRef<HTMLButtonElement>(null)
  // Aktives Feld sichtbar halten (auf schmalen Geräten liegt das Tastenfeld unten darüber)
  useEffect(() => {
    if (aktiv) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [aktiv])
  return (
    <button
      ref={ref}
      type="button"
      onClick={onWaehlen}
      aria-pressed={aktiv}
      className={cn(
        "grid min-h-[76px] min-w-0 scroll-mb-[420px] scroll-mt-[150px] content-between rounded-xl border-2 bg-surface px-3 py-2 text-left transition-colors lg:scroll-mb-4",
        aktiv ? "border-accent shadow-[0_0_0_3px_rgb(var(--accent)/0.2)]" : "border-hair/10",
      )}
      style={ampel && wert ? { background: AMPEL_HINTERGRUND[ampel] } : undefined}
    >
      <span className="flex items-baseline justify-between gap-2 text-[13px] text-ink2">
        <span className="truncate">{label}</span>
        {ampel && wert ? <span className="shrink-0 text-xs font-medium">{AMPEL_TEXT[ampel]}</span> : einheit && <span className="shrink-0">{einheit}</span>}
      </span>
      <span className={cn("tabular font-semibold leading-tight", gross ? "text-[34px]" : "text-[28px]", !wert && "text-muted/60")}>
        {wert || platzhalter || "–"}
        {aktiv && <span className="ml-0.5 inline-block h-7 w-0.5 animate-pulse bg-accent align-middle" />}
        {geaendert && wert && <span className="ml-2 align-middle text-xs font-normal text-accent-strong">geändert</span>}
      </span>
    </button>
  )
}

/** Große Auswahl-Kachel (NC-Typ, Gebinde, Medium, Bemerkung). */
export function Kachel({ an, onClick, children, klein }: { an: boolean; onClick: () => void; children: React.ReactNode; klein?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={an}
      onClick={onClick}
      className={cn(
        "rounded-xl border-2 font-semibold transition-colors active:scale-[0.98]",
        klein ? "min-h-[52px] px-3 text-base" : "min-h-[64px] px-4 text-xl",
        an ? "border-accent bg-accent/15 text-accent-strong" : "border-hair/10 bg-surface text-ink",
      )}
    >
      {children}
    </button>
  )
}
